import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, UserPlus, X } from 'lucide-react';

import BookingCalendar from './BookingCalendar';
import { aFechaISO } from '../lib/calendario';
import './AgregarTurno.css';

/**
 * Cargar a mano los turnos que entraron por fuera de la app —por teléfono, por
 * WhatsApp o en el mostrador—.
 *
 * Esto vivía adentro de "bloquear horario", y ahí no entraba: un bloqueo no
 * tiene cliente, así que no había dónde poner el nombre ni el teléfono; pedía
 * una hora de fin, que para un turno no se elige porque sale de la duración del
 * servicio; y como cada fila se identificaba por su fecha, no se podían cargar
 * dos turnos el mismo día.
 *
 * El horario se elige con `BookingCalendar`, el MISMO componente que usa la
 * página pública, y no con un `input type="time"`. La diferencia no es estética:
 * un input de hora te deja escribir 03:47 de un domingo cerrado, mientras que el
 * calendario ofrece solamente los horarios que existen de verdad —los del
 * horario del negocio, sin los turnos ya tomados, sin los bloqueos y en pasos de
 * la duración del servicio—.
 *
 * Lo que no se reusa de la página pública es su orquestación de pasos (qué
 * mostrar, personal disponible, modos de recurso), que vive suelta en sus ~2.300
 * líneas. Acá el servicio y el personal se eligen arriba del calendario y listo:
 * quien carga es el dueño, que ya sabe qué está agendando.
 *
 * El día y el servicio quedan puestos después de agregar. Pasar en limpio los
 * cinco turnos que anotaste en papel es el caso para el que existe la lista, y
 * volver a elegir la fecha cada vez era el trabajo que esta pantalla vino a
 * sacar.
 */

const CLIENTE_VACIO = { nombre: '', telefono: '', email: '' };

export default function AgregarTurno({
  abierto,
  onCerrar,
  onConfirmar,
  negocio,
  servicios = [],
  personal = [],
  recursos = [],
  guardando,
  error,
}) {
  const [servicioId, setServicioId] = useState('');
  const [personaId, setPersonaId] = useState('');
  const [elegido, setElegido] = useState(null); // lo que devuelve BookingCalendar
  const [cliente, setCliente] = useState(CLIENTE_VACIO);
  const [turnos, setTurnos] = useState([]);
  const [mandarMail, setMandarMail] = useState(true);
  const columnaIzquierda = useRef(null);
  const columnaDerecha = useRef(null);
  // Si ya había un horario elegido en el render anterior. Es lo que distingue
  // "recién eligió" de "sigue elegido", que es cuando NO hay que mover nada.
  const habiaHorario = useRef(false);

  useEffect(() => {
    if (!abierto) return;
    setServicioId('');
    setPersonaId('');
    setElegido(null);
    setCliente(CLIENTE_VACIO);
    setTurnos([]);
    setMandarMail(true);
  }, [abierto]);

  /**
   * Al tocar un día, trae los horarios a la vista.
   *
   * La grilla del mes mide ~600px de alto, así que los horarios quedan abajo del
   * borde visible de la columna y hay que scrollear a mano para verlos —o peor,
   * no se nota que aparecieron—.
   *
   * Va por delegación en el contenedor y no tocando BookingCalendar, que lo
   * comparte la página pública. El retraso es porque los horarios se piden al
   * servidor: se intenta de nuevo unas veces hasta que la sección exista, en vez
   * de apostar a un solo tiempo fijo.
   */
  useEffect(() => {
    const caja = columnaIzquierda.current;
    if (!abierto || !caja) return undefined;

    let pendientes = [];

    const alTocarDia = (e) => {
      const dia = e.target.closest('.calendar-day');
      if (!dia || dia.classList.contains('empty')) return;

      let intentos = 0;
      const traer = () => {
        const horarios = caja.querySelector('.time-slots-section');
        if (horarios) {
          horarios.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          return;
        }
        if (++intentos < 6) pendientes.push(setTimeout(traer, 150));
      };
      pendientes.push(setTimeout(traer, 120));
    };

    caja.addEventListener('click', alTocarDia);
    return () => {
      caja.removeEventListener('click', alTocarDia);
      pendientes.forEach(clearTimeout);
    };
  }, [abierto, servicioId]);

  /**
   * Apilado, al elegir un horario baja la vista hasta los datos del cliente.
   *
   * Con las dos columnas lado a lado el formulario aparece al costado y ya está
   * a la vista, pero apilado queda abajo del pliegue: el calendario y los
   * horarios se comen el alto visible (a 929×900 el cuerpo muestra 694px de 738;
   * a 375, 742 de 933). Entonces tocás un horario y no pasa nada visible —lo que
   * se habilitó está fuera de pantalla— y hay que bajar a mano para enterarse de
   * que el paso siguiente ya está listo.
   *
   * Tres cuidados:
   *
   * 1. Sólo apilado, y se pregunta por la POSICIÓN REAL de las dos columnas en
   *    vez del ancho de ventana. El panel lateral se come 280px, así que el
   *    mismo viewport da anchos distintos y un `matchMedia` acertaría a veces
   *    (el mismo problema que resolvió `.barra-turnos-caja` con container
   *    queries). Si la derecha arranca donde termina la izquierda, están
   *    apiladas; si arrancan a la misma altura, están al costado.
   * 2. Sólo en la transición a "hay horario". `elegido` es un objeto nuevo en
   *    cada render de BookingCalendar, así que sin esta memoria el efecto se
   *    volvería a disparar solo y te devolvería abajo mientras leés arriba.
   *    También evita el tirón al cambiar de horario con el formulario ya
   *    visible.
   * 3. `prefers-reduced-motion` apaga el desplazamiento suave, igual que
   *    `--mov-base` en index.css.
   */
  useEffect(() => {
    const hayHorario = Boolean(elegido);
    const recienElegido = hayHorario && !habiaHorario.current;
    habiaHorario.current = hayHorario;

    if (!abierto || !recienElegido) return;

    const derecha = columnaDerecha.current;
    const izquierda = columnaIzquierda.current;
    if (!derecha || !izquierda) return;

    const apilado =
      derecha.getBoundingClientRect().top >=
      izquierda.getBoundingClientRect().bottom - 1;
    if (!apilado) return;

    const sinMovimiento = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    derecha.scrollIntoView({
      behavior: sinMovimiento ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [abierto, elegido]);

  useEffect(() => {
    if (!abierto) return undefined;

    const alApretar = (e) => {
      if (e.key === 'Escape' && turnos.length === 0) onCerrar();
    };
    document.addEventListener('keydown', alApretar);

    const scrollPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', alApretar);
      document.body.style.overflow = scrollPrevio;
    };
  }, [abierto, onCerrar, turnos.length]);

  if (!abierto) return null;

  const serviciosActivos = servicios.filter((s) => s.active !== false);
  const servicio = serviciosActivos.find((s) => s.id === servicioId);
  const persona = personal.find((p) => p.id === personaId);

  /* BookingCalendar sólo ofrece horarios en las franjas donde hay al menos una
     persona con horario PROPIO habilitado (getCombinedTimeRanges: si no junta
     ningún rango del personal, devuelve vacío y no cae al horario del negocio).

     Y el panel rellena a quien no tiene horario propio con los siete días en
     `enabled: false` (Dashboard.jsx:1111). Para el editor de personal eso es
     correcto —significa "sin configurar"—, pero acá se combinan y el resultado es
     que TODOS los días quedan marcados sin turnos.

     Así que ese relleno se reemplaza por el horario del negocio: si a alguien no
     se le puso un horario propio, trabaja cuando el negocio abre. */
  const tieneHorarioPropio = (persona) =>
    persona?.schedule && Object.values(persona.schedule).some((dia) => dia?.enabled);

  const personalParaCalendario = personal.map((p) =>
    tieneHorarioPropio(p) ? p : { ...p, schedule: negocio?.schedule }
  );

  // BookingCalendar espera el negocio con su personal y sus lugares adentro; en
  // el panel esas tres cosas viven en estados separados.
  const negocioParaCalendario = {
    ...negocio,
    staff: personalParaCalendario,
    resources: recursos,
  };

  // BookingCalendar entrega `date` como objeto Date, no como texto. Se normaliza
  // acá, en el borde, con el mismo helper que usa la página pública: serializar
  // un Date a JSON lo manda como UTC y un turno de la mañana en Argentina se
  // guardaría el día anterior.
  const fechaElegida = elegido ? aFechaISO(elegido.date) : '';

  const claveDe = (fecha, hora) => `${fecha}T${hora}`;
  const yaCargado = elegido
    ? turnos.some((t) => t.clave === claveDe(fechaElegida, elegido.time))
    : false;

  const completo = Boolean(elegido && cliente.nombre.trim() && !yaCargado);

  const agregar = () => {
    setTurnos((previos) => [
      ...previos,
      {
        clave: claveDe(fechaElegida, elegido.time),
        fecha: fechaElegida,
        hora: elegido.time,
        servicio: elegido.service,
        // Si no se eligió a nadie, el calendario pudo asignar personal solo.
        persona: elegido.staff || null,
        recurso: elegido.resource || null,
        cliente: {
          nombre: cliente.nombre.trim(),
          telefono: cliente.telefono.trim(),
          email: cliente.email.trim(),
        },
      },
    ]);

    // El servicio y el personal quedan; se limpian el horario y el cliente. El
    // calendario se queda en el mismo día porque no se lo remonta.
    setElegido(null);
    setCliente(CLIENTE_VACIO);
  };

  const quitar = (clave) => setTurnos((previos) => previos.filter((t) => t.clave !== clave));

  const hayMails = turnos.some((t) => t.cliente.email);

  const formatearFecha = (clave) => {
    const texto = new Date(`${clave}T00:00:00`).toLocaleDateString('es-AR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
    return texto.charAt(0).toUpperCase() + texto.slice(1);
  };

  const confirmar = () => {
    onConfirmar(
      {
        turnos: turnos.map((t) => ({
          service: t.servicio,
          date: t.fecha,
          time: t.hora,
          customer: {
            name: t.cliente.nombre,
            phone: t.cliente.telefono,
            email: t.cliente.email,
          },
          staff: t.persona,
        })),
        enviarMail: mandarMail && hayMails,
      },
      () => setTurnos([])
    );
  };

  return (
    <div
      className="turno-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && turnos.length === 0) onCerrar();
      }}
    >
      <div
        className="turno-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="turno-titulo"
      >
        <header className="turno-panel__cabecera">
          <h3 id="turno-titulo">
            <UserPlus size={18} aria-hidden="true" />
            Agregar turno
          </h3>
          <button type="button" onClick={onCerrar} aria-label="Cerrar">
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="turno-panel__cuerpo">
          <section className="turno-form" ref={columnaIzquierda}>
            {/* El servicio va primero porque de su duración salen los horarios
                que después ofrece el calendario. */}
            <div className="turno-form__arriba">
              <label>
                <span>Servicio</span>
                <select value={servicioId} onChange={(e) => setServicioId(e.target.value)}>
                  <option value="">Elegí uno</option>
                  {serviciosActivos.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                      {s.duration ? ` · ${s.duration} min` : ''}
                    </option>
                  ))}
                </select>
              </label>

              {personal.length > 0 && (
                <label>
                  <span>Personal</span>
                  <select value={personaId} onChange={(e) => setPersonaId(e.target.value)}>
                    <option value="">Cualquiera</option>
                    {personal.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            {!servicio ? (
              <p className="turno-form__espera">
                Elegí un servicio y acá aparecen los horarios libres.
              </p>
            ) : (
              <div className="turno-form__calendario">
                <BookingCalendar
                  // Remontar sólo cuando cambia el servicio o el personal: los
                  // horarios dependen de los dos. Sin la key el calendario se
                  // quedaría mostrando los del servicio anterior.
                  key={`${servicio.id}-${personaId}`}
                  businessInfo={negocioParaCalendario}
                  services={[servicio]}
                  selectedStaff={
                    // Con el horario ya resuelto: si va el original sin horario propio,
                    // BookingCalendar toma el camino de "personal específico" y se
                    // queda otra vez sin rangos.
                    personalParaCalendario.find((p) => p.id === personaId) || null
                  }
                  selectedResource={null}
                  onSelectSlot={setElegido}
                />
              </div>
            )}

          </section>

          <section className="turno-derecha" ref={columnaDerecha}>
            {/* Los datos del cliente viven acá y no abajo del calendario a
                propósito. El calendario mide ~600px de alto, así que un
                formulario debajo caía fuera de la vista y había que scrollear
                para verlo; y esta columna estaba vacía mientras la otra se
                ahogaba. Al tocar un horario el formulario aparece al costado, ya
                visible.

                Eso vale mientras las dos columnas entren lado a lado. Por debajo
                de 1100px se apilan y el formulario vuelve a caer abajo del
                pliegue: ahí lo trae a la vista el efecto de arriba. */}
            {!elegido ? (
              <div className="turno-derecha__espera">
                <h4>Datos del cliente</h4>
                <p>
                  {servicio
                    ? 'Elegí un día y un horario y acá cargás quién viene.'
                    : 'Empezá eligiendo el servicio.'}
                </p>
              </div>
            ) : (
              <div className="turno-derecha__form">
                <h4>Datos del cliente</h4>

                <p className="turno-form__elegido">
                  {formatearFecha(fechaElegida)} a las {elegido.time}
                  {elegido.staff?.name && ` · ${elegido.staff.name}`}
                </p>

                {yaCargado && (
                  <p className="turno-form__aviso">
                    Ya cargaste un turno en ese día y horario.
                  </p>
                )}

                <label>
                  <span>Nombre</span>
                  <input
                    type="text"
                    value={cliente.nombre}
                    autoFocus
                    onChange={(e) => setCliente((c) => ({ ...c, nombre: e.target.value }))}
                    placeholder="Juan Pérez"
                  />
                </label>

                <label>
                  <span>Teléfono</span>
                  <input
                    type="tel"
                    value={cliente.telefono}
                    onChange={(e) => setCliente((c) => ({ ...c, telefono: e.target.value }))}
                    placeholder="11 5555-5555"
                  />
                </label>

                <label>
                  <span>
                    Email <small>(opcional)</small>
                  </span>
                  <input
                    type="email"
                    value={cliente.email}
                    onChange={(e) => setCliente((c) => ({ ...c, email: e.target.value }))}
                    placeholder="Para mandarle la confirmación"
                  />
                </label>

                <button
                  type="button"
                  className="turno-form__agregar"
                  onClick={agregar}
                  disabled={!completo}
                >
                  <Plus size={17} aria-hidden="true" />
                  Agregar y cargar otro
                </button>
              </div>
            )}

            <div className="turno-lista">
              <h4>
                Turnos a cargar
                {turnos.length > 0 && (
                  <span className="turno-lista__cuenta">{turnos.length}</span>
                )}
              </h4>

              {turnos.length === 0 ? (
                <p className="turno-lista__vacia">
                  Los turnos que vayas agregando aparecen acá y se guardan todos juntos al
                  final. El servicio y el día quedan puestos, así cargar varios seguidos es
                  tocar horario, nombre y agregar.
                </p>
              ) : (
                <>
                  <ul>
                    {turnos.map((turno) => (
                      <li key={turno.clave}>
                        <div>
                          <p className="turno-lista__cuando">
                            {formatearFecha(turno.fecha)} · {turno.hora}
                          </p>
                          <p className="turno-lista__cliente">{turno.cliente.nombre}</p>
                          <p className="turno-lista__detalle">
                            {turno.servicio?.name}
                            {turno.persona?.name && ` · ${turno.persona.name}`}
                            {turno.cliente.telefono && ` · ${turno.cliente.telefono}`}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => quitar(turno.clave)}
                          aria-label={`Quitar el turno de ${turno.cliente.nombre}`}
                        >
                          <Trash2 size={15} aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>

                  <p className="turno-lista__seguir">
                    Podés seguir agregando: elegí otro horario.
                  </p>
                </>
              )}
            </div>
          </section>
        </div>

        <footer className="turno-panel__pie">
          {error && <p className="turno-error">{error}</p>}

          {hayMails && (
            <label className="turno-panel__mail">
              <input
                type="checkbox"
                checked={mandarMail}
                onChange={(e) => setMandarMail(e.target.checked)}
              />
              <span>Mandarles la confirmación por mail</span>
            </label>
          )}

          <button type="button" className="turno-cancelar" onClick={onCerrar}>
            Cancelar
          </button>

          <button
            type="button"
            className="turno-confirmar"
            onClick={confirmar}
            disabled={turnos.length === 0 || guardando}
          >
            {guardando
              ? 'Guardando…'
              : turnos.length > 1
              ? `Guardar los ${turnos.length}`
              : 'Guardar turno'}
          </button>
        </footer>
      </div>
    </div>
  );
}
